'use client';

import { forwardRef, useContext, useId, useMemo } from 'react';

import { css, cx } from '@/styled-system/css';

import { Slot, Slottable } from '../slot';
import { FormFieldContext } from './context/form-field.context';
import { formFieldStyles } from './styles/form-field.styles';
import type { FormFieldContextInterface, FormFieldPropsInterface } from './types/form-field.types';

/**
 * @related [`FormHelp`, `Label`, `Tooltip`]
 * @since 4.0.0
 */

export const FormField = forwardRef<HTMLDivElement, FormFieldPropsInterface>(
  (props: FormFieldPropsInterface, forwardedRef) => {
    const {
      asChild = false,
      children,
      className,
      control,
      controlId,
      css: cssProp,
      description,
      disabled = false,
      error = false,
      label,
      readOnly = false,
      required = false,
      requiredText = 'asterisk',
      optional = false,
      tooltip,
      ...rest
    } = props;

    const styles = css(formFieldStyles, cssProp);
    const Component = asChild ? Slot : 'div';

    const defaultId = useId();
    const descriptionId = useId();
    const labelId = useId();

    const parentContext = useContext(FormFieldContext);
    const hasDescription = Boolean(description);
    const currentContext = useMemo<FormFieldContextInterface>(
      () => ({
        controlId: controlId ?? defaultId,
        labelId,
        required,
        requiredText,
        optional,
        disabled,
        error,
        readOnly,
        ...(hasDescription ? { descriptionId: parentContext.descriptionId ?? descriptionId } : {}),
      }),
      [
        controlId,
        defaultId,
        labelId,
        required,
        requiredText,
        optional,
        disabled,
        error,
        readOnly,
        hasDescription,
        parentContext.descriptionId,
        descriptionId,
      ],
    );

    return (
      <FormFieldContext.Provider value={currentContext}>
        <Component
          ref={forwardedRef}
          data-c11n-component="FormField"
          data-c11n-version={__C11N_VERSION__}
          className={cx('c11n', 'c11n_form-field', styles, className)}
          {...rest}
        >
          {label}
          {tooltip}
          {control}
          <Slottable>{children}</Slottable>
          {description}
        </Component>
      </FormFieldContext.Provider>
    );
  },
);

FormField.displayName = 'FormField';
